import { useState, useEffect } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { formatDateDisplay } from '../../utils/dateUtils';
import {
  Box,
  Heading,
  Text,
  Button,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Link,
  Select,
  Input,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  Center,
  Tabs,
  TabList,
  Tab,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import {
  AddIcon,
  ChevronDownIcon,
  CloseIcon,
  SearchIcon,
} from '@chakra-ui/icons';
import ticketService from '../../services/ticketService';
import projectService from '../../services/projectService';
import usersService from '../../services/usersService';
import { useAuth } from '../../contexts/AuthContext';
import DueDateDisplay from '../../components/DueDateDisplay';

const TicketsList = () => {
  const toast = useToast();
  const { user } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'all') return 1;
    return 0;
  };

  const getInitialFilters = () => {
    return {
      status: searchParams.get('status') || '',
      priority: searchParams.get('priority') || '',
      project: searchParams.get('project') || '',
      search: searchParams.get('search') || '',
    };
  };

  const getInitialSort = () => {
    return searchParams.get('sort') || '-priority,dueDate,-createdAt';
  };

  const getInitialPage = () => {
    return parseInt(searchParams.get('page'), 10) || 1;
  };

  const getInitialLimit = () => {
    return parseInt(searchParams.get('limit'), 10) || 20;
  };

  const [tickets, setTickets] = useState([]);
  const [pagination, setPagination] = useState({
    page: getInitialPage(),
    limit: getInitialLimit(),
    totalPages: 1,
  });
  const [pageLimit, setPageLimit] = useState(getInitialLimit());
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [assignToUser, setAssignToUser] = useState('');
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState(getInitialFilters());
  const [sortBy, setSortBy] = useState(getInitialSort());
  const [activeTab, setActiveTab] = useState(getInitialTab);

  const updateUrlParams = (updatedFilters, updatedSort, updatedPage, updatedLimit, updatedTab) => {
    const newSearchParams = new URLSearchParams();
    
    if (updatedTab === 1) {
      newSearchParams.set('tab', 'all');
    }
    
    if (updatedFilters.status) newSearchParams.set('status', updatedFilters.status);
    if (updatedFilters.priority) newSearchParams.set('priority', updatedFilters.priority);
    if (updatedFilters.project) newSearchParams.set('project', updatedFilters.project);
    if (updatedFilters.search) newSearchParams.set('search', updatedFilters.search);
    
    if (updatedSort !== '-priority,dueDate,-createdAt') {
      newSearchParams.set('sort', updatedSort);
    }
    
    if (updatedPage > 1) {
      newSearchParams.set('page', updatedPage.toString());
    }
    
    if (updatedLimit !== 20) {
      newSearchParams.set('limit', updatedLimit.toString());
    }
    
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Fetch all projects for the filter dropdown
        const projectsResponse = await projectService.getProjects();
        setProjects(projectsResponse.data.data);

        // Fetch users for both admin and support roles
        if ((user?.role === 'admin' || user?.role === 'support') && activeTab === 1) {
          const usersResponse = await usersService.getUsers({
            'role': 'admin,support'
          });
          setUsers(usersResponse.data.data);
        }

        // Fetch tickets with any active filters
        const queryParams = {};
        if (filters.status) queryParams.status = filters.status;
        if (filters.priority) queryParams.priority = filters.priority;
        if (filters.project) queryParams.project = filters.project;
        if (filters.organization) queryParams.organization = filters.organization;
        if (filters.search) queryParams.search = filters.search;

        // Use different endpoint based on active tab
        let ticketsResponse;
        if (activeTab === 0) {
          // My Tickets
          ticketsResponse = await ticketService.getMyTickets({
            ...queryParams,
            page: pagination.page,
            limit: pageLimit,
            sort: sortBy
          });
        } else {
          // All Tickets
          ticketsResponse = await ticketService.getTickets({
            ...queryParams,
            page: pagination.page,
            limit: pageLimit,
            sort: sortBy
          });
        }

        const { data, pagination: apiPagination } = ticketsResponse.data;

        setPagination((prev) => ({
          ...prev,
          totalPages: apiPagination.totalPages,
        }));
        setTickets(data ?? []);
      } catch (error) {
        toast({
          title: 'Error fetching data',
          description: error.response?.data?.message || 'Something went wrong',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [filters, sortBy, pagination.page, pageLimit, activeTab, user.role, toast]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    const updatedFilters = {
      ...filters,
      [name]: value
    };
    setFilters(updatedFilters);
    
    // Reset pagination to page 1 when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Update URL parameters
    updateUrlParams(updatedFilters, sortBy, 1, pageLimit, activeTab);
  };

  const resetFilters = () => {
    const clearedFilters = {
      status: '',
      priority: '',
      project: '',
      search: '',
    };
    setFilters(clearedFilters);
    
    // Reset pagination to page 1 when filters are reset
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Update URL parameters
    updateUrlParams(clearedFilters, sortBy, 1, pageLimit, activeTab);
  };

  const handlePageLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setPageLimit(newLimit);
    
    // Reset to page 1 when changing page limit
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Update URL parameters
    updateUrlParams(filters, sortBy, 1, newLimit, activeTab);
  };

  // Helper function to get the appropriate badge color for ticket status
  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'blue';
      case 'in-progress':
        return 'yellow';
      case 'resolved':
        return 'green';
      case 'closed':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Helper function to get the appropriate badge color for ticket priority
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'red';
      case 'medium':
        return 'yellow';
      case 'low':
        return 'green';
      default:
        return 'gray';
    }
  };


  // Open the modal for ticket assignment
  const handleOpenModal = (ticket) => {
    setSelectedTicket(ticket);
    setAssignToUser(ticket.assignedTo?._id || '');
    onOpen();
  };

  // Handle ticket assignment
  const handleAssignTicket = async () => {
    if (!selectedTicket) return;

    try {
      setIsUpdating(true);
      await ticketService.assignTicket(selectedTicket._id, assignToUser);

      // Update the local state
      setTickets(tickets.map(ticket => {
        if (ticket._id === selectedTicket._id) {
          return {
            ...ticket,
            assignedTo: assignToUser ? users.find(user => user._id === assignToUser) : null
          };
        }
        return ticket;
      }));

      toast({
        title: 'Ticket assigned',
        description: assignToUser ? `Ticket has been assigned successfully` : `Ticket has been unassigned successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Error assigning ticket',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleExport = async (format) => {
    try {
      setIsExporting(true);
      const queryParams = {};
      if (filters.status) queryParams.status = filters.status;
      if (filters.priority) queryParams.priority = filters.priority;
      if (filters.project) queryParams.project = filters.project;
      if (filters.organization) queryParams.organization = filters.organization;
      if (filters.search) queryParams.search = filters.search;
      queryParams.sort = sortBy;

      const resp = activeTab === 0
        ? await ticketService.exportMyTickets(queryParams, format)
        : await ticketService.exportAllTickets(queryParams, format);

      const blob = resp.data;
      const cd = resp.headers['content-disposition'];
      let filename = `tickets_${activeTab === 0 ? 'my' : 'all'}.${format}`;
      if (cd) {
        const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
        if (match) {
          filename = decodeURIComponent(match[1] || match[2]);
        }
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Export started',
        description: `Downloading ${filename}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error.response?.data?.message || 'Unable to export tickets',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Tickets</Heading>
          <Text color="gray.600">View and manage support tickets</Text>
        </Box>
        {user?.role !== 'support' && (
          <Button
            as={RouterLink}
            to="/tickets/new"
            colorScheme="brand"
            leftIcon={<AddIcon />}
          >
            Create New Ticket
          </Button>
        )}
      </Flex>

      <Tabs
        index={activeTab}
        onChange={(index) => {
          setActiveTab(index);
          setPagination(prev => ({ ...prev, page: 1 }));
          updateUrlParams(filters, sortBy, 1, pageLimit, index);
        }}
        mb={6}
        colorScheme="brand"
      >
        <TabList>
          <Tab>My Tickets</Tab>
          <Tab>All Tickets</Tab>
        </TabList>
      </Tabs>

      {/* Filters */}
      <Box
        bg="white"
        p={4}
        borderRadius="md"
        boxShadow="sm"
        mb={6}
      >
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={4}
        >
          <HStack flex={{ base: '1', md: '3' }}>
            <Box position="relative" width="100%">
              <Input
                name="search"
                placeholder="Search tickets..."
                value={filters.search}
                onChange={handleFilterChange}
                pl={10}
              />
              <Box position="absolute" left={3} top="50%" transform="translateY(-50%)">
                <SearchIcon color="gray.400" />
              </Box>
            </Box>
          </HStack>

          <HStack flex={{ base: '1', md: '5' }} spacing={3} wrap={{ base: 'wrap', md: 'nowrap' }}>
            <Select
              name="status"
              placeholder="Status: All"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Select>

            <Select
              name="priority"
              placeholder="Priority: All"
              value={filters.priority}
              onChange={handleFilterChange}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>

            <Select
              name="project"
              placeholder="Project: All"
              value={filters.project}
              onChange={handleFilterChange}
            >
              {projects.map(project => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </Select>

            <Select
              value={sortBy}
              onChange={(e) => {
                const newSort = e.target.value;
                setSortBy(newSort);
                updateUrlParams(filters, newSort, pagination.page, pageLimit, activeTab);
              }}
              bg="gray.50"
              borderColor="gray.300"
              width="200px"
              flexShrink={0}
            >
              <option value="-priority,dueDate,-createdAt">Priority High → Low</option>
              <option value="priority,dueDate,-createdAt">Priority Low → High</option>
              <option value="dueDate,-createdAt">Due Date (Soonest)</option>
              <option value="-dueDate,-createdAt">Due Date (Latest)</option>
              <option value="-createdAt">Newest First</option>
              <option value="createdAt">Oldest First</option>
              <option value="status,-createdAt">Status</option>
            </Select>

            {(filters.status || filters.priority || filters.project || filters.search) && (
              <IconButton
                aria-label="Reset filters"
                icon={<CloseIcon />}
                onClick={resetFilters}
                variant="ghost"
              />
            )}

            {user?.role === 'admin' && (
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  colorScheme="brand"
                  isDisabled={isExporting}
                  minWidth="120px"
                  flexShrink={0}
                >
                  Export
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => handleExport('json')} isDisabled={isExporting}>
                    Export JSON
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Tickets Table */}
      <Box
        bg="white"
        borderRadius="md"
        boxShadow="sm"
        overflow="hidden"
      >
        {isLoading ? (
          <Center p={8}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="gray.200"
              color="brand.500"
              size="xl"
            />
          </Center>
        ) : (
          <>
            <Box overflowX="auto">
              <Table variant="simple" size="sm" minW="max-content">
                <Thead bg="white" borderBottomWidth="1px" borderColor="gray.200" height={35}>
                  <Tr>
                    <Th>Title</Th>
                    <Th>Project</Th>
                    {activeTab === 1 && <Th>Submitted By</Th>}
                    {activeTab === 1 && (user?.role === 'admin' || user?.role === 'support') && <Th>Assigned To</Th>}
                    <Th>Status</Th>
                    <Th>Priority</Th>
                    <Th>Created</Th>
                    <Th>Due Date</Th>
                    <Th>Resolved</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tickets.length > 0 ? (
                    tickets.map((ticket) => (
                      <Tr key={ticket._id}>
                        <Td maxW="300px" isTruncated>
                          <Link
                            as={RouterLink}
                            to={`/tickets/${ticket._id}`}
                            color="brand.600"
                            fontWeight="medium"
                          >
                            {ticket.title}
                          </Link>
                        </Td>
                        <Td>{ticket.project.name}</Td>
                        {activeTab === 1 && <Td>{ticket.submittedBy?.name || 'Unknown'}</Td>}
                        {activeTab === 1 && (user?.role === 'admin' || user?.role === 'support') && (
                          <Td>
                            {ticket.assignedTo ? (
                              ticket.assignedTo.name
                            ) : (
                              <Text color="gray.400">Unassigned</Text>
                            )}
                          </Td>
                        )}
                        <Td>
                          <Badge colorScheme={getStatusColor(ticket.status)}>
                            {ticket.status}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getPriorityColor(ticket.priority)}>
                            {ticket.priority}
                          </Badge>
                        </Td>
                        <Td>
                          {formatDateDisplay(ticket.createdAt)}
                        </Td>
                        <Td>
                          <DueDateDisplay dueDate={ticket.dueDate} ticketStatus={ticket.status} />
                        </Td>
                        <Td>
                          {ticket.resolvedDate ? formatDateDisplay(ticket.resolvedDate) : '-'}
                        </Td>
                        <Td>
                          <Menu>
                            <MenuButton
                              as={Button}
                              rightIcon={<ChevronDownIcon />}
                              size="sm"
                              variant="outline"
                            >
                              Actions
                            </MenuButton>
                            <MenuList>
                              <MenuItem
                                as={RouterLink}
                                to={`/tickets/${ticket._id}`}
                              >
                                View
                              </MenuItem>
                              {user?.role !== 'support' && (
                                <MenuItem
                                  as={RouterLink}
                                  to={`/tickets/${ticket._id}/edit`}
                                >
                                  Edit
                                </MenuItem>
                              )}
                              {(user?.role === 'admin' || user?.role === 'support') && activeTab === 1 && (
                                <MenuItem onClick={() => handleOpenModal(ticket)}>
                                  Assign Ticket
                                </MenuItem>
                              )}
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={activeTab === 1 ? (user?.role === 'admin' || user?.role === 'support' ? 10 : 9) : 8} textAlign="center" py={4}>
                        <Text color="gray.500">
                          No tickets found. {activeTab === 0 ? 'You haven\'t created any tickets yet.' : 'No tickets match your filters.'}
                        </Text>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
            <Flex justify="space-between" align="center" p={4} bg="white">
              <HStack spacing={4}>
                <Text>
                  Page {pagination.page} of {pagination.totalPages}
                </Text>
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.600">Show:</Text>
                  <Select
                    size="sm"
                    value={pageLimit}
                    onChange={handlePageLimitChange}
                    width="80px"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Select>
                  <Text fontSize="sm" color="gray.600">per page</Text>
                </HStack>
              </HStack>
              <HStack spacing={2}>
                <Button
                  size="sm"
                  onClick={() => {
                    const newPage = pagination.page - 1;
                    setPagination((p) => ({ ...p, page: newPage }));
                    updateUrlParams(filters, sortBy, newPage, pageLimit, activeTab);
                  }}
                  isDisabled={pagination.page <= 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    const newPage = pagination.page + 1;
                    setPagination((p) => ({ ...p, page: newPage }));
                    updateUrlParams(filters, sortBy, newPage, pageLimit, activeTab);
                  }}
                  isDisabled={pagination.page >= pagination.totalPages}
                >
                  Next
                </Button>
              </HStack>
            </Flex>
          </>
        )}
      </Box>

      {/* Assign Ticket Modal */}
      {selectedTicket && (
        <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Assign Ticket</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={4}>
                Assign ticket "<strong>{selectedTicket.title}</strong>" to:
              </Text>
              <Select
                value={assignToUser ?? ''}
                onChange={(e) => setAssignToUser(e.target.value === '' ? null : e.target.value)}
                placeholder="Select staff member"
              >
                <option value={''}>Unassigned</option>
                {users.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                isLoading={isUpdating}
                onClick={handleAssignTicket}
              >
                Assign Ticket
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default TicketsList;
